(() => {
  const root = document.documentElement;
  const STORAGE_KEY = 'pc-theme-mode';
  const VALID = new Set(['auto', 'light', 'dark']);
  const media = window.matchMedia('(prefers-color-scheme: dark)');

  const getDefaultMode = () => {
    const value = root.dataset.pcThemeDefault || 'auto';
    return VALID.has(value) ? value : 'auto';
  };

  const getStoredMode = () => {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return VALID.has(value) ? value : null;
    } catch (_) {
      return null;
    }
  };

  const getMode = () => {
    const value = root.dataset.pcThemeMode || getStoredMode() || getDefaultMode();
    return VALID.has(value) ? value : 'auto';
  };

  const resolveMode = (mode) => (mode === 'auto' ? (media.matches ? 'dark' : 'light') : mode);

  function syncControls() {
    const mode = getMode();
    const resolved = resolveMode(mode);

    document.querySelectorAll('[data-pc-theme-option]').forEach((button) => {
      const selected = button.dataset.pcThemeOption === mode;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    document.querySelectorAll('[data-pc-theme-trigger]').forEach((button) => {
      const modeLabel = mode === 'auto' ? `Automatic (${resolved})` : resolved[0].toUpperCase() + resolved.slice(1);
      button.setAttribute('aria-label', `Appearance: ${modeLabel}. Change appearance`);
      button.setAttribute('title', `Appearance: ${modeLabel}`);
    });
  }

  function applyMode(mode, { persist = false, announce = true } = {}) {
    if (!VALID.has(mode)) mode = 'auto';
    const resolved = resolveMode(mode);

    root.dataset.pcThemeMode = mode;
    root.dataset.pcTheme = resolved;

    if (persist) {
      try { localStorage.setItem(STORAGE_KEY, mode); } catch (_) {}
    }

    syncControls();

    if (announce) {
      window.dispatchEvent(new CustomEvent('procommerce:themechange', {
        detail: { mode, resolved }
      }));
    }
  }

  function closeAll(except = null) {
    document.querySelectorAll('[data-pc-theme-switcher]').forEach((switcher) => {
      if (switcher === except) return;
      switcher.dataset.open = 'false';
      const trigger = switcher.querySelector('[data-pc-theme-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-pc-theme-trigger]');
    if (trigger) {
      const switcher = trigger.closest('[data-pc-theme-switcher]');
      if (!switcher) return;
      const willOpen = switcher.dataset.open !== 'true';
      closeAll(switcher);
      switcher.dataset.open = willOpen ? 'true' : 'false';
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        requestAnimationFrame(() => switcher.querySelector('[data-pc-theme-option][aria-pressed="true"]')?.focus());
      }
      return;
    }

    const option = event.target.closest('[data-pc-theme-option]');
    if (option) {
      applyMode(option.dataset.pcThemeOption, { persist: true });
      closeAll();
      return;
    }

    if (!event.target.closest('[data-pc-theme-switcher]')) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    const switcher = event.target.closest?.('[data-pc-theme-switcher]');
    if (!switcher) return;

    if (event.key === 'Escape') {
      closeAll();
      switcher.querySelector('[data-pc-theme-trigger]')?.focus();
      return;
    }

    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const options = [...switcher.querySelectorAll('[data-pc-theme-option]')];
    const current = options.indexOf(document.activeElement);
    if (current === -1) return;
    event.preventDefault();
    const step = event.key === 'ArrowDown' ? 1 : -1;
    options[(current + step + options.length) % options.length].focus();
  });

  const onSystemChange = () => {
    if (getMode() === 'auto') applyMode('auto', { persist: false });
  };
  if (media.addEventListener) media.addEventListener('change', onSystemChange);
  else media.addListener(onSystemChange);

  applyMode(getMode(), { persist: false, announce: false });
  requestAnimationFrame(() => root.classList.add('pc-theme-ready'));

  document.addEventListener('shopify:section:load', syncControls);

  // Mobile/touch service cards need an explicit selected state because hover
  // does not exist on phones. A normal click event is generated by taps on
  // iOS/Android as well, which makes this more reliable across mobile browsers.
  const serviceTapSelector = '[class*="-process__step"], [class*="-deliverables__item"]';
  const serviceActiveSelector = '[class*="-process__step"].is-touch-active, [class*="-deliverables__item"].is-touch-active';
  const touchLikeQuery = window.matchMedia('(hover: none), (pointer: coarse)');

  function activateServiceCard(card) {
    const section = card.closest('section') || document;
    section.querySelectorAll(serviceActiveSelector).forEach((item) => {
      if (item !== card) item.classList.remove('is-touch-active');
    });
    card.classList.add('is-touch-active');
  }

  document.addEventListener('click', (event) => {
    if (!touchLikeQuery.matches) return;
    const card = event.target.closest?.(serviceTapSelector);
    if (card) {
      activateServiceCard(card);
      return;
    }
    document.querySelectorAll(serviceActiveSelector).forEach((item) => item.classList.remove('is-touch-active'));
  });
})();
